import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { showError, showSuccess } from '../utils/sweetAlert';
import logger from '../utils/logger';

const RecommendationsPage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [existingProjects, setExistingProjects] = useState({});

  useEffect(() => {
    fetchRecommendations();
    fetchExistingProjects();
  }, [assessmentId]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}/recommendations?report_type=paid`,
        { withCredentials: true }
      );
      logger.log(response,'response')
      
      setRecommendations(response.data.recommendations || []);
      setSummary(response.data.summary || {});
      
      // Try to fetch assessment info for context (if endpoint exists)
      try {
        const assessmentResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/esa/results/${assessmentId}`,
          { withCredentials: true }
        );
        setAssessmentInfo(assessmentResponse.data);
      } catch (err) {
        logger.log('Could not fetch assessment details:', err);
        // Set minimal info from URL
        setAssessmentInfo({ id: assessmentId });
      }
      
    } catch (error) {
      logger.error('Error fetching recommendations:', error);
      showError('Error', 'Failed to load recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingProjects = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/comprehensive`,
        { withCredentials: true }
      );
      
      // Build a map of recommendation_id -> project for quick lookup
      const projectMap = {};
      (response.data || []).forEach(project => {
        let meta = project.meta;
        if (typeof meta === 'string') {
          try {
            meta = JSON.parse(meta);
          } catch (e) {
            meta = {};
          }
        }
        const recId = meta?.original_recommendation_id;
        if (recId) {
          projectMap[recId] = project;
        }
      });
      
      setExistingProjects(projectMap);
      logger.log('📋 Existing projects loaded:', Object.keys(projectMap).length);
    } catch (error) {
      logger.error('Error fetching existing projects:', error);
    }
  };

  const handleAddToProject = (recommendation) => {
    // Check if project already exists
    const existingProject = existingProjects[recommendation.original_recommendation_id];

    if (existingProject) {
      // Navigate to existing project
      navigate(`/projects/${existingProject.id}`);
      showSuccess('Project Exists', `This recommendation is already added to project: "${existingProject.title}"`);
    } else {
      // Store recommendation in sessionStorage for the modal
      sessionStorage.setItem('pendingProject', JSON.stringify(recommendation));
      // Navigate to projects page which will show the modal
      navigate('/projects?action=create');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Lighting': '💡',
      'Motors & Drives': '⚙️',
      'HVAC Systems': '🌡️',
      'Compressed Air': '💨',
      'Steam Systems': '♨️',
      'Insulation': '🧱',
      'Energy Efficiency': '⚡'
    };
    return icons[category] || '⚡';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 dark:border-emerald-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading recommendations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Energy Savings Recommendations
              </h1>
              {assessmentInfo && (
                <p className="text-gray-600 dark:text-gray-400">
                  {assessmentInfo.facility_name} - {assessmentInfo.industry_name}
                </p>
              )}
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Recommendations</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary.total_recommendations}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Investment</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                ${summary.total_potential_cost?.toLocaleString()}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Annual Savings</div>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                ${summary.total_annual_savings?.toLocaleString()}/yr
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Payback</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary.average_payback?.toFixed(1)} years
              </div>
            </div>
          </div>
        )}

        {/* Recommendations Grid */}
        {recommendations.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No recommendations available for this assessment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                {/* Priority Badge */}
                <div className="px-6 pt-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                    {rec.priority.toUpperCase()} PRIORITY
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Title & Category */}
                  <div className="flex items-start mb-3">
                    <span className="text-2xl mr-2">{getCategoryIcon(rec.category)}</span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {rec.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{rec.category}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {rec.description}
                  </p>

                  {/* Financial Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        ${rec.implementation_cost_aud?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Annual Savings:</span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        ${rec.annual_savings_aud?.toLocaleString()}/yr
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Energy Savings:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {rec.annual_savings_kwh?.toLocaleString()} kWh/yr
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Payback:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {rec.payback_years} years
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">ROI:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {rec.roi_percent}%
                      </span>
                    </div>
                  </div>

                  {/* Add to Project Button */}
                  {existingProjects[rec.original_recommendation_id] ? (
                    <button
                      onClick={() => handleAddToProject(rec)}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Already Added - View Project
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAddToProject(rec)}
                      className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors duration-200"
                    >
                      Add to Project
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationsPage;
